import PropTypes from 'prop-types'
import { useEffect, useState, useRef } from 'react'

const InstagramEmbedV2 = ({ videoSrc, className }) => {
  const [isOverlayVisible, setIsOverlayVisible] = useState(true)
  const [isValid, setIsValid] = useState(true)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isMobileOrIOS, setIsMobileOrIOS] = useState(false)
  const firstVideoHeight = useRef(null)
  const blockquoteRef = useRef(null)

  // Detectar si es dispositivo móvil o iOS
  useEffect(() => {
    const checkDevice = () => {
      const userAgent = navigator.userAgent
      const isIOS = /iPad|iPhone|iPod/.test(userAgent)
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)
      const isSmallScreen = window.innerWidth <= 768
      
      setIsMobileOrIOS(isIOS || isMobile || isSmallScreen)
    }
    
    checkDevice()
    window.addEventListener('resize', checkDevice)
    
    return () => window.removeEventListener('resize', checkDevice)
  }, [])

  const loadInstagram = async () => {
    if (typeof document !== 'undefined' && !document.getElementById('igScript')) {
      const igScript = document.createElement('script')
      igScript.setAttribute('src', '//www.instagram.com/embed.js')
      igScript.setAttribute('id', 'igScript')
      igScript.setAttribute('async', '')
      document.head.appendChild(igScript)
    }
  }

  // const handleVideoAspectRatio = () => {
  //   const videoContainer = document.querySelector('.instagram-media.instagram-media-rendered')
  //   if (videoContainer) {
  //     const videoHeight = videoContainer.offsetHeight
  //     const newWidth = videoHeight * (13 / 16)
  //     console.log(newWidth)
  //     videoContainer.style.width = `${newWidth}px`
  //   }
  // }

  const handleIframeWidth = () => {
    const videoContainer = document.querySelector('.instagram-media.instagram-media-rendered')
    if (videoContainer) {
      let videoHeight
      if(!firstVideoHeight.current) {
        videoHeight = videoContainer.getAttribute('height')
        if(videoHeight !== '0') firstVideoHeight.current = videoHeight
      }

      const definitiveVideoHeight = firstVideoHeight.current || videoHeight

      // 1
      if(definitiveVideoHeight < 700 && definitiveVideoHeight > 500) {
        const newWidth = definitiveVideoHeight * (1)
        videoContainer.style.width = `${newWidth}px`
      }
      // 1.7
      else if(definitiveVideoHeight < 500) {
        const newWidth = definitiveVideoHeight * (2.4)
        videoContainer.style.width = `${newWidth}px`
      }
      // 10/16
      else {
        const newWidth = definitiveVideoHeight * (10 / 16)
        videoContainer.style.width = `${newWidth}px`
      }

      const timer = setTimeout(() => {
        setIsOverlayVisible(false)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }

  useEffect(() => {
    async function verifyLink() {
      try {
        const response = await fetch(videoSrc, { method: 'HEAD', mode: 'no-cors' })

        if (response.type === 'opaque' || response.ok) {
          setIsValid(true)
        } else {
          setIsValid(false)
          setIsOverlayVisible(false)
        }
      } catch (error) {
        setIsValid(false)
        setIsOverlayVisible(false)
      }
    }

    verifyLink()

    // Only load Instagram if not mobile/iOS
    if (!isMobileOrIOS) {
      loadInstagram().then(() => {
        if (typeof window.instgrm !== 'undefined') window.instgrm.Embeds.process()
      })
    } else {
      // For mobile/iOS, only show the overlay for 500ms
      const timer = setTimeout(() => {
        setIsOverlayVisible(false)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [videoSrc, isMobileOrIOS])

  useEffect(() => {
    if (blockquoteRef.current && !isMobileOrIOS) {
      const observer = new MutationObserver(() => {
        setIsLoaded(true)
      })

      observer.observe(blockquoteRef.current, { childList: true, subtree: true })

      window.addEventListener('resize', handleIframeWidth)

      return () => observer.disconnect()
    }
  }, [blockquoteRef, isMobileOrIOS])

  useEffect(() => {
    let interval
    if(!firstVideoHeight.current && !isMobileOrIOS) {
      interval = setInterval(() => {
        handleIframeWidth()
      }, 1000)
    } else {
      clearInterval(interval)
    }
    return () => clearInterval(interval)
  }, [firstVideoHeight, isMobileOrIOS])

  // Mobile/iOS rendering
  if (isMobileOrIOS) {
    return (
      <div
        className={`VideoDetailPage__VideoContainer ${isOverlayVisible ? 'Loading' : ''} ${className}`}
        style={{
          position: 'relative',
          height: '75vh',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {isOverlayVisible && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'black',
            zIndex: 1,
          }} />
        )}
        {(isValid && videoSrc) ? (
          <blockquote>
            <div>
              <a 
                href={videoSrc} 
                style={{
                  background: 'transparent',
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div style={{
                  display: 'block',
                  height: '80px',
                  margin: '0 auto 20px',
                  width: '80px'
                }}>
                  <svg width="80px" height="80px" viewBox="0 0 100 100" version="1.1">
                    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                      <polygon fill="#fff" points="25,15 25,85 80,50"/>
                    </g>
                  </svg>
                </div>
                <div style={{
                  color: '#fff',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: '600',
                }}>
                  Watch on Instagram
                </div>
              </a>
            </div>
          </blockquote>
        ) : (
          <div className='d-flex text-2 align-items-center justify-content-center text-center' style={{
            color: 'white',
            zIndex: 2,
            boxShadow: '0 0 1px 0 rgba(0,0,0,0.5), 0 1px 10px 0 rgba(0,0,0,0.15)',
            backgroundColor: 'black',
            width: '100%',
            height: '100%',
          }}>
            <p>This video is no longer available or the link is incorrect.</p>
          </div>
        )}
      </div>
    )
  }

  // Desktop rendering (Instagram iframe)
  return (
    <div
      className={`VideoDetailPage__VideoContainer ${isOverlayVisible ? 'Loading' : ''} ${className}`}
      style={{
        position: 'relative',
        // paddingTop: '60px',
        cursor: 'pointer'
      }}
    >
      {isOverlayVisible && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'black',
          zIndex: 1,
        }} />
      )}
      {(isValid && videoSrc) ? (
        <blockquote
          ref={blockquoteRef}
          className="instagram-media"
          data-instgrm-permalink={videoSrc}
          data-instgrm-version="14"
          // data-instgrm-width="402"
          // data-instgrm-height="500"
          frameBorder="0"
          style={{
            background: '#FFF',
            border: 0,
            margin: 0,
            padding: 0,
            // height: 'calc(100% + 208px)',
            // width: '402px',
            maxWidth: '100%',
            boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)',
            position: 'relative',
            overflow: 'hidden',
            zIndex: 0,
            display: isLoaded ? 'block' : 'none',
          }}
        >
          <a href={videoSrc} />
        </blockquote>
      ) : (
        <div className='d-flex text-2 align-items-center justify-content-center text-center' style={{
          color: 'white',
          zIndex: 2,
          boxShadow: '0 0 1px 0 rgba(0,0,0,0.5), 0 1px 10px 0 rgba(0,0,0,0.15)',
          backgroundColor: 'black',
          width: '100%',
          height: '100%',
        }}>
          <p>This video is no longer available or the link is incorrect.</p>
        </div>
      )}
    </div>
  )
}

InstagramEmbedV2.propTypes = {
  videoSrc: PropTypes.string.isRequired,
  className: PropTypes.string,
}

export default InstagramEmbedV2